import { CellContext } from '@tanstack/react-table';
import React, { CSSProperties } from 'react';

import APLTableCell from '@/components/available-preview-list/apl-table-cell/APLTableCell';
import { ColorCell, UniqueId } from '@/types/ui.types';

interface CellFactoryProps<T> {
  cellContext: CellContext<T, unknown>;
  text: string;
  actionIcon?: React.ReactNode;
  actionIconClickHandler?: (id: UniqueId) => void;
  cellContentInjector?: (item: CellContext<T, unknown>) => React.JSX.Element;
  className?: string;
  clickHandler?: (e: React.MouseEvent, id: UniqueId) => void;
  injectedContentOverridesText?: boolean;
  isSelected?: boolean;
  style?: CSSProperties;
}

// This is the maximum string length that permits a three character
// ellipsis without changing table row widths (550px)
const MAX_STRING_LENGTH = 79;

const formatCellText = (text: string): string => {
  // If cell text is too long, trim it to the max length
  // and append an ellipsis to indicate omission
  if (text.length >= MAX_STRING_LENGTH) {
    // eslint-disable-next-line no-magic-numbers
    return text.substring(0, MAX_STRING_LENGTH).concat('...');
  }

  return text;
};

export const cellFactory = <
  T extends {
    id: UniqueId;
  } & Partial<ColorCell>,
>(
  cellProps: CellFactoryProps<T>,
) => {
  // eslint-disable-next-line react/prop-types
  const {
    actionIcon,
    actionIconClickHandler,
    cellContext,
    className,
    clickHandler,
    cellContentInjector = undefined,
    injectedContentOverridesText = false,
    isSelected = false,
    style,
    text,
  } = cellProps;

  // eslint-disable-next-line react/prop-types
  if (!cellContext.row?.original) return null;

  return (
    <APLTableCell
      actionIconClickHandler={
        actionIconClickHandler ? () => actionIconClickHandler(cellContext.row.original.id) : undefined
      }
      className={className}
      color={cellContext.row.original.color}
      colorText={cellContext.row.original.colorText}
      injectedCellContent={cellContentInjector?.(cellContext)}
      icon={actionIcon ?? undefined}
      injectedContentOverridesText={injectedContentOverridesText}
      isSelected={isSelected}
      onClick={clickHandler ? (e) => clickHandler(e, cellContext.row.original.id) : undefined}
      style={
        style ?? {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }
      }
      text={formatCellText(text)}
    />
  );
};
